import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, Users, FolderKanban, Activity, DollarSign, LogOut, RefreshCw, Search, Clock, CheckCircle, XCircle, Crown, FileCheck, BarChart3, AlertTriangle, UserPlus, TrendingUp } from 'lucide-react';
import { AdminAnalytics } from '../components/AdminAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [projects, setProjects] = useState({ projects: [], total: 0 });
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('adminToken');
    return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token };
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  }, [navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(API_URL + '/api/admin/dashboard', { headers: getAuthHeaders() });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) { handleLogout(); return; }
        throw new Error('Failed to fetch dashboard');
      }
      setDashboard(await response.json());
    } catch (err) { setError(err.message); }
  };

  const fetchUsers = async () => {
    try {
      let url = API_URL + '/api/admin/users?limit=50';
      if (searchTerm) url += '&search=' + encodeURIComponent(searchTerm);
      if (tierFilter) url += '&tier=' + tierFilter;
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch users');
      setUsers(await response.json());
    } catch (err) { setError(err.message); }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(API_URL + '/api/admin/projects?limit=50', { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch projects');
      setProjects(await response.json());
    } catch (err) { setError(err.message); }
  };

  const fetchActivity = async () => {
    try {
      const response = await fetch(API_URL + '/api/admin/activity?limit=20', { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch activity');
      const data = await response.json();
      setActivity(data.activity || []);
    } catch (err) { setError(err.message); }
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchUsers(), fetchProjects(), fetchActivity()]);
    setLoading(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
    refreshData();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [searchTerm, tierFilter, activeTab]);

  const updateUserField = async (userId, field, value) => {
    try {
      const response = await fetch(API_URL + '/api/admin/users/' + userId, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ [field]: value })
      });
      if (!response.ok) throw new Error('Failed to update user');
      fetchUsers();
    } catch (err) { alert(err.message); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'cyan' }) => (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-zinc-400 text-sm mb-1">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subValue && <p className="text-xs text-zinc-500 mt-1">{subValue}</p>}
        </div>
        <div className={'p-2 rounded-lg ' + (color === 'cyan' ? 'bg-cyan-500/10' : color === 'purple' ? 'bg-purple-500/10' : color === 'green' ? 'bg-green-500/10' : 'bg-yellow-500/10')}>
          <Icon className={'w-5 h-5 ' + (color === 'cyan' ? 'text-cyan-400' : color === 'purple' ? 'text-purple-400' : color === 'green' ? 'text-green-400' : 'text-yellow-400')} />
        </div>
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={dashboard?.overview?.total_users || 0} subValue={(dashboard?.overview?.active_users || 0) + ' active'} />
        <StatCard icon={FolderKanban} label="Total Projects" value={dashboard?.overview?.total_projects || 0} color="purple" />
        <StatCard icon={FileCheck} label="Audits Completed" value={dashboard?.overview?.total_audits || 0} color="green" />
        <StatCard icon={DollarSign} label="Monthly Revenue" value={'$' + (dashboard?.subscriptions?.mrr || 0)} subValue="Estimated MRR" color="yellow" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-yellow-400" />Subscription Tiers</h3>
          <div className="space-y-4">
            {[{ tier: 'Free', count: dashboard?.subscriptions?.free, color: 'bg-zinc-500' }, { tier: 'Growth ($49)', count: dashboard?.subscriptions?.growth, color: 'bg-cyan-500' }, { tier: 'Enterprise ($299)', count: dashboard?.subscriptions?.enterprise, color: 'bg-purple-500' }].map(function(item) { return (
              <div key={item.tier} className="flex items-center justify-between">
                <span className="text-zinc-400">{item.tier}</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={'h-full rounded-full ' + item.color} style={{ width: ((item.count || 0) / (dashboard?.overview?.total_users || 1) * 100) + '%' }} />
                  </div>
                  <span className="text-white font-medium w-12 text-right">{item.count || 0}</span>
                </div>
              </div>
            ); })}
          </div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-cyan-400" />Projects by Phase</h3>
          <div className="space-y-4">
            {[1, 2, 3, 4].map(function(phase) { return (
              <div key={phase} className="flex items-center justify-between">
                <span className="text-zinc-400">Phase {phase}</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full" style={{ width: ((dashboard?.projects_by_phase?.['phase' + phase] || 0) / (dashboard?.overview?.total_projects || 1) * 100) + '%' }} />
                  </div>
                  <span className="text-white font-medium w-12 text-right">{dashboard?.projects_by_phase?.['phase' + phase] || 0}</span>
                </div>
              </div>
            ); })}
          </div>
        </div>
      </div>
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Activity className="w-5 h-5 text-green-400" />Recent Activity (Last 7 Days)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-800/50 rounded-lg p-4 text-center"><UserPlus className="w-8 h-8 text-cyan-400 mx-auto mb-2" /><p className="text-2xl font-bold text-white">{dashboard?.recent_activity?.new_users_7d || 0}</p><p className="text-sm text-zinc-400">New Users</p></div>
          <div className="bg-zinc-800/50 rounded-lg p-4 text-center"><FolderKanban className="w-8 h-8 text-purple-400 mx-auto mb-2" /><p className="text-2xl font-bold text-white">{dashboard?.recent_activity?.new_projects_7d || 0}</p><p className="text-sm text-zinc-400">New Projects</p></div>
          <div className="bg-zinc-800/50 rounded-lg p-4 text-center"><FileCheck className="w-8 h-8 text-green-400 mx-auto mb-2" /><p className="text-2xl font-bold text-white">{dashboard?.recent_activity?.new_audits_7d || 0}</p><p className="text-sm text-zinc-400">Audits Run</p></div>
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input type="text" placeholder="Search users..." value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} className="pl-10 bg-zinc-800/50 border-zinc-700 text-white" data-testid="admin-user-search" />
        </div>
        <select value={tierFilter} onChange={function(e) { setTierFilter(e.target.value); }} className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="admin-tier-filter">
          <option value="">All Tiers</option>
          <option value="free">Free</option>
          <option value="growth">Growth</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <span className="text-zinc-400 text-sm">{users.total} users total</span>
      </div>
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">User</th>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Tier</th>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Projects</th>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Joined</th>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {users.users.map(function(user) { return (
                <tr key={user.id} className="hover:bg-zinc-800/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">{user.username?.charAt(0).toUpperCase()}</div>
                      <div><p className="text-white text-sm font-medium flex items-center gap-1">{user.username}{user.is_admin && <Shield className="w-3 h-3 text-yellow-400" />}</p><p className="text-zinc-500 text-xs">{user.email}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select value={user.subscription_tier} onChange={function(e) { updateUserField(user.id, 'subscription_tier', e.target.value); }} className={'text-xs px-2 py-1 rounded-full border-0 ' + (user.subscription_tier === 'enterprise' ? 'bg-purple-500/20 text-purple-400' : user.subscription_tier === 'growth' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-zinc-700 text-zinc-300')}>
                      <option value="free">Free</option>
                      <option value="growth">Growth</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {user.is_active ? <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle className="w-3 h-3" /> Active</span> : <span className="flex items-center gap-1 text-red-400 text-xs"><XCircle className="w-3 h-3" /> Disabled</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">{user.project_count || 0}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{formatDate(user.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={function() { updateUserField(user.id, 'is_active', !user.is_active); }} className={'p-1 rounded ' + (user.is_active ? 'text-red-400 hover:bg-red-500/10' : 'text-green-400 hover:bg-green-500/10')} title={user.is_active ? 'Disable User' : 'Enable User'}>{user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}</button>
                      <button onClick={function() { updateUserField(user.id, 'is_admin', !user.is_admin); }} className={'p-1 rounded ' + (user.is_admin ? 'text-yellow-400 hover:bg-yellow-500/10' : 'text-zinc-400 hover:bg-zinc-700')} title={user.is_admin ? 'Remove Admin' : 'Make Admin'}><Shield className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h3 className="text-lg font-semibold text-white">All Projects ({projects.total})</h3></div>
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Project</th>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Owner</th>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Phase</th>
                <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {projects.projects.map(function(project) { return (
                <tr key={project.id} className="hover:bg-zinc-800/30 transition-colors">
                  <td className="px-4 py-3"><p className="text-white text-sm font-medium">{project.name || 'Unnamed'}</p></td>
                  <td className="px-4 py-3">{project.user ? <span className="text-zinc-300 text-sm">{project.user.username}</span> : <span className="text-zinc-500 text-sm">Guest</span>}</td>
                  <td className="px-4 py-3"><span className={'px-2 py-1 rounded-full text-xs ' + (project.current_phase === 4 ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-300')}>Phase {project.current_phase}</span></td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{formatDate(project.created_at)}</td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderActivity = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Activity className="w-5 h-5 text-green-400" />Activity Feed</h3>
      <div className="space-y-3">
        {activity.map(function(item, idx) { return (
          <div key={idx} className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 flex items-start gap-4">
            <div className={'p-2 rounded-lg ' + (item.type === 'user_registration' ? 'bg-cyan-500/10' : item.type === 'project_created' ? 'bg-purple-500/10' : 'bg-green-500/10')}>
              {item.type === 'user_registration' && <UserPlus className="w-4 h-4 text-cyan-400" />}
              {item.type === 'project_created' && <FolderKanban className="w-4 h-4 text-purple-400" />}
              {item.type === 'audit_completed' && <FileCheck className="w-4 h-4 text-green-400" />}
            </div>
            <div className="flex-1"><p className="text-white text-sm">{item.description}</p><p className="text-zinc-500 text-xs mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(item.timestamp)}</p></div>
          </div>
        ); })}
        {activity.length === 0 && <p className="text-zinc-500 text-center py-8">No recent activity</p>}
      </div>
    </div>
  );

  if (loading && !dashboard) {
    return <div className="min-h-screen bg-[#09090b] flex items-center justify-center"><RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="bg-zinc-900/80 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4 group">
            <div className="relative">
              <div className="absolute inset-0 bg-[#00f0ff]/25 blur-2xl rounded-full" />
              <img src="/zerozone-logo.png" alt="ZeroZone Logo" className="relative w-36 h-36 object-contain drop-shadow-[0_0_24px_rgba(59,130,246,0.6)]" />
            </div>
            <div><h1 className="text-white font-semibold">Admin Dashboard</h1><p className="text-zinc-500 text-xs">ZeroZone Global Resilience Cloud Architect</p></div>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={refreshData} variant="ghost" size="sm" className="text-zinc-400 hover:text-white" disabled={loading}><RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />Refresh</Button>
            <span className="text-zinc-400 text-sm">{adminUser.email}</span>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-zinc-400 hover:text-red-400" data-testid="admin-logout-button"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{error}<button onClick={function() { setError(''); }} className="ml-auto">x</button></div>}
        <div className="flex gap-2 mb-6 border-b border-zinc-800 pb-4">
          {[{ id: 'overview', label: 'Overview', icon: BarChart3 }, { id: 'analytics', label: 'Analytics', icon: TrendingUp }, { id: 'users', label: 'Users', icon: Users }, { id: 'projects', label: 'Projects', icon: FolderKanban }, { id: 'activity', label: 'Activity', icon: Activity }].map(function(tab) { return (
            <button key={tab.id} onClick={function() { setActiveTab(tab.id); }} className={'px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ' + (activeTab === tab.id ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30' : 'text-zinc-400 hover:text-white hover:bg-zinc-800')} data-testid={'admin-tab-' + tab.id}><tab.icon className="w-4 h-4" />{tab.label}</button>
          ); })}
        </div>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'analytics' && <AdminAnalytics dashboard={dashboard} getAuthHeaders={getAuthHeaders} onAuthError={handleLogout} />}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'projects' && renderProjects()}
        {activeTab === 'activity' && renderActivity()}
      </div>
    </div>
  );
}
